import { io, Socket } from 'socket.io-client';

export interface WorldTickData {
  gameTime: number;
  dayPhase: string;
  entities: GameEntity[];
  tick: number;
  policeAlertLevel: number;
}

export interface GameEntity {
  id: string;
  type: string;
  gridX: number;
  gridY: number;
  targetGridX: number;
  targetGridY: number;
  speed: number;
  direction: string;
  state: string;
  color: number;
}

export interface ActionResult {
  success: boolean;
  error?: string;
  newState?: {
    moneyDirty: number;
    moneyClean: number;
    level: number;
  };
  data?: Record<string, unknown>;
}

export interface TileData {
  gridX: number;
  gridY: number;
  tileType: string;
}

export interface BuildingData {
  id: number;
  type: string;
  name: string;
  gridX: number;
  gridY: number;
  ownerId: string | null;
  interactable: boolean;
  assignedRole: string | null;
}

export interface MapData {
  tiles: TileData[];
  buildings: BuildingData[];
  gridWidth: number;
  gridHeight: number;
}

export interface BuildingStateData {
  building: BuildingData;
  businessInfo?: BusinessInfo;
  dealPointInfo?: DealPointInfo;
  wholesalerInfo?: WholesalerInfo;
  growRoomInfo?: GrowRoomInfo;
  stashInfo?: StashInfo;
  policeInfo?: PoliceInfo;
  casinoInfo?: CasinoInfo;
  multiHandBlackjackState?: MultiHandBlackjackState;
  crapsState?: CrapsState;
  estimatedRevenue: number;
  suspicionLevel: number;
}

export interface BusinessInfo {
  id: number;
  businessType: string;
  reputationScore: number;
  legitimacyCursor: number;
  suspicionLevel: number;
  isClosedUntil: string | null;
  launderingQueue: LaunderingQueueInfo[];
  dailyCapacity: number;
  currentRatio: number;
}

export interface LaunderingQueueInfo {
  id: number;
  dirtyCashDeposited: number;
  expectedCleanCash: number;
  progress: number;
  availableAt: string | null;
  isReady: boolean;
}

export interface DealPointInfo {
  id: number;
  drugType: string;
  pricePerUnit: number;
  stockOnSite: number;
  riskLevel: number;
  isEnemy: boolean;
}

export interface WholesalerInfo {
  trustLevel: number;
  xp: number;
  nextLevelXp: number;
  availableProducts: WholesalerProduct[];
}

export interface WholesalerProduct {
  drugType: string;
  name: string;
  pricePerUnit: number;
  requiredLevel: number;
  inStock: number;
}

export interface PoliceInfo {
  alertLevel: number;
  watchedBuildings: { buildingId: number; businessId: number; name: string; riskPercent: number }[];
  corruptionCost: number;
  corruptionSuccessRate: number;
  reduceAlertCost: number;
  canReduceAlert: boolean;
}

export interface GrowRoomInfo {
  id: number;
  capacity: number;
  currentPlants: number;
  batches: PlantBatchInfo[];
}

export interface PlantBatchInfo {
  id: number;
  strain: string;
  quantity: number;
  stage: string;
  health: number;
  targetHumidity: number;
  targetTemperature: number;
  targetLightHours: number;
  currentHumidity: number;
  currentTemperature: number;
  currentLightHours: number;
  ticksInStage: number;
  ticksRequired: number;
  estimatedYield: number;
  harvestedYield: number;
  deathReason: string | null;
  endedAt: string | null;
}

export interface StashInfo {
  id: number;
  capacity: number;
  usedCapacity: number;
  stocks: { drugType: string; quantity: number }[];
}

export interface CasinoInfo {
  id: number;
  ownerId: string | null;
  houseEdge: { slots: number; blackjack: number; roulette: number; craps: number };
  levels: { machines: number; tables: number; bar: number };
  stars: number;
  attendance: number;
  suspicion: number;
  totalRevenue: number;
  totalLaundered: number;
  upgradeCosts: { machines: number; tables: number; bar: number };
}

export interface BlackjackHandState {
  handIndex: number;
  cards: { suit: string; value: string }[];
  score: number;
  betAmount: number;
  usedDirty: boolean;
  canHit: boolean;
  canDoubleDown: boolean;
  canSplit: boolean;
  canSurrender: boolean;
  isSplitHand: boolean;
  spotIndex: number;
  splitDepth: number;
  isAceSplit: boolean;
  insuranceBet: number;
  insuranceDecision: 'PENDING' | 'ACCEPTED' | 'DECLINED';
  insuranceResult: number;
  isComplete: boolean;
  outcome?: string;
  payout?: number;
}

export interface MultiHandBlackjackState {
  hands: BlackjackHandState[];
  dealerCards: { suit: string; value: string; faceDown?: boolean }[];
  dealerScore: number | null;
  activeHandIndex: number;
  isAllComplete: boolean;
  insuranceOffered: boolean;
  insurancePhaseComplete: boolean;
  totalPayout?: number;
}

export interface CrapsState {
  phase: 'COME_OUT' | 'POINT';
  point: number | null;
  dice: [number, number] | null;
  activeBets: CrapsBet[];
  isComplete: boolean;
  results?: CrapsBetResult[];
}

export interface CrapsBet {
  type: string;
  amount: number;
  useDirty: boolean;
  number?: number;
}

export interface CrapsBetResult {
  bet: CrapsBet;
  outcome: string;
  payout: number;
}

export interface SlotWinningLine {
  lineIndex: number;
  symbol: string;
  count: number;
  payout: number;
}

export interface SlotResult {
  grid: string[][];        // 5 columns × 3 rows: grid[col][row]
  isWin: boolean;
  payout: number;
  multiplier: number;
  winningLines: SlotWinningLine[];
}

export interface RouletteResult {
  winningNumber: number;
  color: 'RED' | 'BLACK' | 'GREEN';
  betResults: { type: string; outcome: string; payout: number }[];
}

export interface CasinoGameResult {
  gameType: string;
  outcome: string;
  payout: number;
  newClean: number;
  newDirty: number;
  details: SlotResult | MultiHandBlackjackState | RouletteResult | CrapsState;
}

type TickCallback = (data: WorldTickData) => void;
type ActionCallback = (data: ActionResult) => void;
type BuildingStateCallback = (data: BuildingStateData) => void;
type GenericCallback = (data: Record<string, unknown>) => void;

export class SocketManager {
  private socket: Socket | null = null;
  private token: string = '';
  private tickCallbacks: TickCallback[] = [];
  private actionCallbacks: ActionCallback[] = [];
  private buildingStateCallbacks: BuildingStateCallback[] = [];
  private launderingCallbacks: GenericCallback[] = [];
  private suspicionCallbacks: GenericCallback[] = [];
  private businessClosedCallbacks: GenericCallback[] = [];
  private growUpdateCallbacks: GenericCallback[] = [];
  private stageChangedCallbacks: GenericCallback[] = [];
  private plantDeadCallbacks: GenericCallback[] = [];
  private harvestReadyCallbacks: GenericCallback[] = [];

  connect(token: string, persist = true): void {
    this.token = token;
    if (persist) {
      localStorage.setItem('mafia_token', token);
    }
    this.socket = io(window.location.origin, {
      auth: { token },
      transports: ['websocket'],
    });

    this.socket.on('WORLD_TICK', (data: WorldTickData) => {
      for (const cb of this.tickCallbacks) cb(data);
    });

    this.socket.on('ACTION_RESULT', (data: ActionResult) => {
      console.log(`[SOCKET] ACTION_RESULT: success=${data.success}, error=${data.error || 'none'}, hasData=${!!data.data}, hasNewState=${!!data.newState}`);
      for (const cb of this.actionCallbacks) cb(data);
    });

    this.socket.on('BUILDING_STATE', (data: BuildingStateData) => {
      for (const cb of this.buildingStateCallbacks) cb(data);
    });

    this.socket.on('LAUNDERING_UPDATE', (data: Record<string, unknown>) => {
      for (const cb of this.launderingCallbacks) cb(data);
    });

    this.socket.on('SUSPICION_UPDATE', (data: Record<string, unknown>) => {
      for (const cb of this.suspicionCallbacks) cb(data);
    });

    this.socket.on('BUSINESS_CLOSED', (data: Record<string, unknown>) => {
      for (const cb of this.businessClosedCallbacks) cb(data);
    });

    this.socket.on('GROW_UPDATE', (data: Record<string, unknown>) => {
      for (const cb of this.growUpdateCallbacks) cb(data);
    });

    this.socket.on('STAGE_CHANGED', (data: Record<string, unknown>) => {
      for (const cb of this.stageChangedCallbacks) cb(data);
    });

    this.socket.on('PLANT_DEAD', (data: Record<string, unknown>) => {
      for (const cb of this.plantDeadCallbacks) cb(data);
    });

    this.socket.on('HARVEST_READY', (data: Record<string, unknown>) => {
      for (const cb of this.harvestReadyCallbacks) cb(data);
    });

    this.socket.on('AUTH_ERROR', (data: { message: string }) => {
      console.error('Auth error:', data.message);
      this.clearSavedToken();
    });

    this.socket.on('connect_error', (err: Error) => {
      console.error('Connection error:', err.message);
    });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  getSavedToken(): string | null {
    return localStorage.getItem('mafia_token');
  }

  clearSavedToken(): void {
    localStorage.removeItem('mafia_token');
  }

  onTick(callback: TickCallback): void {
    this.tickCallbacks.push(callback);
  }

  onActionResult(callback: ActionCallback): void {
    this.actionCallbacks.push(callback);
  }

  offActionResult(callback: ActionCallback): void {
    this.actionCallbacks = this.actionCallbacks.filter((cb) => cb !== callback);
  }

  onceActionResult(callback: ActionCallback): void {
    const wrapper: ActionCallback = (data) => {
      this.offActionResult(wrapper);
      callback(data);
    };
    this.actionCallbacks.push(wrapper);
  }

  onBuildingState(callback: BuildingStateCallback): void {
    this.buildingStateCallbacks.push(callback);
  }

  onLaunderingUpdate(callback: GenericCallback): void {
    this.launderingCallbacks.push(callback);
  }

  onSuspicionUpdate(callback: GenericCallback): void {
    this.suspicionCallbacks.push(callback);
  }

  onBusinessClosed(callback: GenericCallback): void {
    this.businessClosedCallbacks.push(callback);
  }

  onGrowUpdate(callback: GenericCallback): void {
    this.growUpdateCallbacks.push(callback);
  }

  onStageChanged(callback: GenericCallback): void {
    this.stageChangedCallbacks.push(callback);
  }

  onPlantDead(callback: GenericCallback): void {
    this.plantDeadCallbacks.push(callback);
  }

  onHarvestReady(callback: GenericCallback): void {
    this.harvestReadyCallbacks.push(callback);
  }

  onCasinoGameResult(callback: (data: CasinoGameResult) => void): void {
    this.socket?.on('CASINO_GAME_RESULT', (data: CasinoGameResult) => {
      console.log(`[SOCKET] CASINO_GAME_RESULT: gameType=${data.gameType}, outcome=${data.outcome}, payout=${data.payout}`);
      callback(data);
    });
  }

  onCasinoUpdate(callback: (data: { casinoId: number; attendance: number; revenue: number; suspicion: number }) => void): void {
    this.socket?.on('CASINO_UPDATE', (data: { casinoId: number; attendance: number; revenue: number; suspicion: number }) => {
      console.log(`[SOCKET] CASINO_UPDATE: casinoId=${data.casinoId}, attendance=${data.attendance}, revenue=${data.revenue}, suspicion=${data.suspicion}`);
      callback(data);
    });
  }

  sendAction(type: string, payload: Record<string, unknown>): void {
    console.log(`[SOCKET] sendAction: type=${type}, payload=`, payload);
    this.socket?.emit('ACTION', { type, payload });
  }

  ping(): void {
    this.socket?.emit('PING');
  }

  getToken(): string {
    return this.token;
  }

  getPlayerId(): string | null {
    const token = this.token || localStorage.getItem('mafia_token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub ?? null;
    } catch {
      return null;
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  async fetchMap(): Promise<MapData> {
    const res = await fetch('/api/world/map');
    return res.json() as Promise<MapData>;
  }

  async fetchPlayerState(): Promise<{ moneyDirty: number; moneyClean: number; level: number } | null> {
    try {
      const token = this.token || localStorage.getItem('mafia_token');
      if (!token) return null;
      const res = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { moneyDirty: number; moneyClean: number; level: number };
      console.log(`[SOCKET] fetchPlayerState: dirty=$${data.moneyDirty}, clean=$${data.moneyClean}`);
      return data;
    } catch {
      return null;
    }
  }

  async login(username: string, password: string): Promise<string> {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) throw new Error('Login failed');
    const data = (await res.json()) as { access_token: string; refresh_token: string };
    if (data.refresh_token) {
      localStorage.setItem('mafia_refresh_token', data.refresh_token);
    }
    return data.access_token;
  }

  async register(username: string, password: string): Promise<string> {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const err = (await res.json()) as { message: string };
      throw new Error(err.message || 'Registration failed');
    }
    const data = (await res.json()) as { access_token: string; refresh_token: string };
    if (data.refresh_token) {
      localStorage.setItem('mafia_refresh_token', data.refresh_token);
    }
    return data.access_token;
  }

  async refreshToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem('mafia_refresh_token');
    if (!refreshToken) return null;
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { access_token: string; refresh_token: string };
      localStorage.setItem('mafia_refresh_token', data.refresh_token);
      return data.access_token;
    } catch {
      return null;
    }
  }
}

export const socketManager = new SocketManager();
